// Admin READ-ONLY proforma viewer — route /all-proformas/:id, reached by
// clicking a proforma number on the All-proformas list.
//
// STRICTLY READ-ONLY: it renders shared/components/ProformaDocument (the
// exact component the customer's /proforma/:token page uses, so the two
// renderings can never drift) with a Download-PDF link beside it, and
// NOTHING else. No editable fields, no price inputs, no line editing, no
// state-changing buttons. Approval and rejection live on the approval
// screen.
//
// Works for every quotation state. A superseded revision renders the FROZEN
// figures that were actually sent (line prices are frozen on
// quotation_lines) — never today's catalog price — and links to the
// revision that replaced it.

import { Link, useParams } from 'react-router-dom';
import apiClient from 'shared/api/client.js';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useProformaDocument,
  useUnsealProformaDocument,
  useRequoteProformaDocument,
} from '../hooks/useAllProformas.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import SealSection from '../components/SealSection.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import { ProformaDocument } from 'shared/components/ProformaDocument/index.js';
import { statusLabel } from '../i18n.js';
import styles from './AllProformasViewPage.module.css';

const NOT_ISSUED_STATUSES = new Set(['draft', 'pending_approval']);

function DocumentBanner({ data, t }) {
  const { quotationStatus, supersededBy } = data;

  // Sealed-bid tender (migration 020): purely informational — this route
  // always returns the FULL document regardless of seal state (staff-only),
  // so this just tells an admin the buyer currently sees only a skeleton.
  // No button, no state-changing control — this page is strictly read-only.
  if (data.document.sealed && !data.document.unsealed_at) {
    return <p className={styles.bannerState}>{t('allProformas.view.sealedNote')}</p>;
  }
  if (quotationStatus === 'superseded') {
    return (
      <p className={styles.bannerSuperseded}>
        {t('allProformas.view.supersededNote')}{' '}
        {supersededBy && (
          <Link to={`/all-proformas/${supersededBy.id}`} className={styles.bannerLink}>
            {supersededBy.proformaNo ||
              t('allProformas.view.theRevision', { n: supersededBy.revision })}
          </Link>
        )}
      </p>
    );
  }
  if (quotationStatus === 'declined') {
    return <p className={styles.bannerState}>{t('allProformas.view.declinedNote')}</p>;
  }
  if (quotationStatus === 'expired') {
    return <p className={styles.bannerState}>{t('allProformas.view.expiredNote')}</p>;
  }
  if (quotationStatus === 'rejected') {
    return <p className={styles.bannerState}>{t('allProformas.view.rejectedNote')}</p>;
  }
  return null;
}

// Re-quote — sits alongside SealSection (not inside it, and not inside the
// read-only ProformaDocument itself), shown only when the GET .../document
// response's `canRequote` is true (admin has no can_requote permission gate,
// unlike the sales console's own gated flag — backend/src/services/
// quotationService.js#getProformaDocument's own comment on this). A small,
// admin-local equivalent of pages/SalesQuotationDetailPage.jsx's
// RequoteBlock — that one is sales-console-only (its own hook/cache,
// ['sales','quotationDetail',id]) and this is the only admin screen that
// needs this action, so a shared extraction isn't a clean fit yet.
function RequoteSection({ requoteMutation }) {
  const { t, confirm, toastOk, toastErr } = useActionFeedback();

  const handleRequote = async () => {
    const ok = await confirm({
      title: t('allProformas.view.requote.confirmTitle'),
      message: t('allProformas.view.requote.confirmBody'),
      confirmLabel: t('allProformas.view.requote.button'),
      tone: 'danger',
    });
    if (!ok) return;

    requoteMutation.mutate(undefined, {
      onSuccess: () => toastOk(t('allProformas.view.requote.successToast')),
      onError: (err) => toastErr(err, ['QUOTATION_NOT_REQUOTABLE', 'QUOTATION_NOT_FOUND']),
    });
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('allProformas.view.requote.title')}</h2>
      <p className={styles.meta}>{t('allProformas.view.requote.hint')}</p>
      <button
        type="button"
        className={styles.actionButton}
        onClick={handleRequote}
        disabled={requoteMutation.isPending}
      >
        {requoteMutation.isPending && <Spinner />}{' '}
        {requoteMutation.isPending
          ? t('allProformas.view.requote.requoting')
          : t('allProformas.view.requote.button')}
      </button>
    </section>
  );
}

export default function AllProformasViewPage() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const { data, isLoading, isError, error, refetch } = useProformaDocument(id);
  // `id` is this page's own useParams() STRING, matching the query key
  // useProformaDocument(id) reads — see useUnsealProformaDocument's own
  // header comment for why this must stay a separate hook/cache from
  // ApprovalDetailPage's useUnsealQuotation.
  const unsealMutation = useUnsealProformaDocument(id);
  const requoteMutation = useRequoteProformaDocument(id);

  return (
    <div className={styles.page}>
      <Link to="/all-proformas" className={styles.back}>
        {t('allProformas.view.back')}
      </Link>

      {isLoading && <LoadingState variant="page" />}
      {isError && !isLoading && (
        <ErrorState messageId="allProformas.view.errorBody" error={error} onRetry={refetch} />
      )}

      {!isLoading && !isError && data && (
        <>
          <p className={styles.readOnlyHint}>{t('allProformas.view.readOnlyHint')}</p>
          <ProformaDocument
            document={data.document}
            lang={lang}
            organizationName={
              lang === 'am' && data.organizationNameAm
                ? data.organizationNameAm
                : data.organizationName
            }
            refNo={data.requestRefNo}
            statusLabel={statusLabel(data.quotationStatus, lang)}
            downloadHref={data.hasPdf ? apiClient.url(`/admin/quotations/${id}/pdf`) : undefined}
            pdfPending={data.pdfPending}
            notIssued={NOT_ISSUED_STATUSES.has(data.quotationStatus)}
            banner={<DocumentBanner data={data} t={t} />}
          />

          {/* Seal/unseal: alongside the read-only document, never inside it
              (this task's brief) — reachable here in ANY quotation state,
              not only pending approval, since this "Quotations" list/detail
              covers every state and is nav-linked (AdminNav's own
              nav.admin.allProformas item, relabeled "Quotations"). */}
          {data.seal?.sealed && (
            <SealSection
              quotation={{
                id: data.document.id,
                sealed_at: data.seal.sealedAt,
                seal_bid_opening_at: data.seal.bidOpeningAt,
                unsealed_at: data.seal.unsealedAt,
                unsealed_by_name: data.seal.unsealedByName,
                unseal_route: data.seal.unsealRoute,
              }}
              requestId={data.requestId}
              unsealMutation={unsealMutation}
            />
          )}

          {data.canRequote && <RequoteSection requoteMutation={requoteMutation} />}
        </>
      )}
    </div>
  );
}
